import React, { Suspense } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import {
  MdSettings,
  MdCorporateFare,
  MdSupport,
  MdAdd,
  MdFileUpload,
} from 'react-icons/md';
// Static imports — needed immediately on app load
import CompanyHomeRedirect from 'views/admin/main/empresa/CompanyHomeRedirect';
import SignInDefault from 'views/auth/signIn/SignInDefault';
import SignUpDefault from 'views/auth/signUp/SignUpDefault';
import { lazyWithRetry } from 'utils/lazyWithRetry';

// Lazy-loaded route components
const UserNew = React.lazy(
  lazyWithRetry(() => import('views/admin/main/users/newUser'), 'user-new')
);
const CompanyProfileDetail = React.lazy(
  lazyWithRetry(
    () => import('views/admin/main/empresas/CompanyProfileDetail'),
    'company-profile-detail'
  )
);
const HelpCenter = React.lazy(
  lazyWithRetry(
    () => import('views/admin/main/support/HelpCenter'),
    'help-center'
  )
);
const NotificationsPage = React.lazy(
  lazyWithRetry(
    () => import('views/admin/main/empresa/NotificationsPage'),
    'notifications-page'
  )
);
const DocumentsPage = React.lazy(
  lazyWithRetry(
    () => import('views/admin/main/empresa/DocumentsPage'),
    'documents-page'
  )
);
const PerksPage = React.lazy(
  lazyWithRetry(
    () => import('views/admin/main/empresa/PerksPage'),
    'perks-page'
  )
);
const MemberChangesPage = React.lazy(
  lazyWithRetry(
    () =>
      import('views/admin/main/empresa/services').then((m) => ({
        default: m.MemberChangesPage,
      })),
    'member-changes-page'
  )
);

// Lazy-loaded heavy pages
const AccountSettings = React.lazy(
  lazyWithRetry(
    () => import('views/admin/main/account/settings'),
    'account-settings'
  )
);
const EmpresasMain = React.lazy(
  lazyWithRetry(() => import('views/admin/main/empresas'), 'empresas-main')
);
const CompanyDashboard = React.lazy(
  lazyWithRetry(
    () => import('views/admin/main/empresa/CompanyDashboard'),
    'company-dashboard'
  )
);
const BankAccountPage = React.lazy(
  lazyWithRetry(
    () => import('views/admin/main/empresa/BankAccountPage'),
    'bank-account-page'
  )
);
const ImportWizard = React.lazy(
  lazyWithRetry(
    () =>
      import('views/admin/main/import').then((m) => ({
        default: m.ImportWizard,
      })),
    'import-wizard'
  )
);

function TaxesRedirect() {
  const { companyId } = useParams();
  return (
    <Navigate
      to={companyId ? `/empresa/${companyId}/dashboard` : '/empresa/home'}
      replace
    />
  );
}
const ImportsListPage = React.lazy(
  lazyWithRetry(
    () =>
      import('views/admin/main/import').then((m) => ({
        default: m.ImportsListPage,
      })),
    'imports-list-page'
  )
);

const routes = [
  // --- Auth pages ---
  {
    name: 'Sign In',
    nameKey: null, // Auth pages don't need translation
    path: '/sign-in/default',
    layout: '/auth',
    component: <SignInDefault />,
    showInNavbar: false,
    showInSidebar: false,
  },
  {
    name: 'Sign Up',
    nameKey: null,
    path: '/sign-up/default',
    layout: '/auth',
    component: <SignUpDefault />,
    showInNavbar: false,
    showInSidebar: false,
  },
  // --- Main pages ---
  {
    name: 'Home',
    nameKey: 'navigation.home',
    path: 'home',
    layout: '/empresa',
    component: <CompanyHomeRedirect />,
    showInNavbar: false,
    showInSidebar: false,
  },
  {
    name: 'Formar Empresa', // Fallback
    nameKey: 'navigation.createCompany',
    path: 'nueva',
    layout: '/empresa',
    icon: <MdAdd className="text-inherit h-5 w-5" />,
    component: (
      <Suspense fallback={<div />}>
        <UserNew />
      </Suspense>
    ),
    showInNavbar: true,
    showInSidebar: true,
  },
  // --- Import Company pages ---
  {
    name: 'Import Company',
    nameKey: 'navigation.importCompany',
    path: 'import/new',
    layout: '/empresa',
    icon: <MdFileUpload className="text-inherit h-5 w-5" />,
    component: (
      <Suspense fallback={<div />}>
        <ImportWizard />
      </Suspense>
    ),
    showInNavbar: false,
    showInSidebar: false,
  },
  {
    name: 'My Imports',
    nameKey: 'navigation.myImports',
    path: 'imports',
    layout: '/empresa',
    component: (
      <Suspense fallback={<div />}>
        <ImportsListPage />
      </Suspense>
    ),
    showInNavbar: false,
    showInSidebar: false,
  },
  {
    name: 'Import Detail',
    nameKey: 'navigation.importDetail',
    path: 'import/:importId',
    layout: '/empresa',
    component: (
      <Suspense fallback={<div />}>
        <ImportWizard />
      </Suspense>
    ),
    showInNavbar: false,
    showInSidebar: false,
  },
  {
    name: 'Empresas',
    nameKey: 'navigation.companies',
    path: 'empresas',
    layout: '/empresa',
    icon: <MdCorporateFare className="text-inherit h-5 w-5" />,
    component: (
      <Suspense fallback={<div />}>
        <EmpresasMain />
      </Suspense>
    ),
    showInNavbar: false,
    showInSidebar: false,
  },
  {
    name: 'Perfil de Empresa', // Fallback
    nameKey: 'navigation.companyProfile',
    path: 'empresas/:registrationId/perfil',
    layout: '/empresa',
    component: (
      <Suspense fallback={<div />}>
        <CompanyProfileDetail />
      </Suspense>
    ),
    showInNavbar: false,
    showInSidebar: false,
  },
  // --- Company-scoped pages (new) ---
  {
    name: 'Dashboard',
    nameKey: 'navigation.dashboard',
    path: ':companyId/dashboard',
    layout: '/empresa',
    component: (
      <Suspense fallback={<div />}>
        <CompanyDashboard />
      </Suspense>
    ),
    showInNavbar: false,
    showInSidebar: false, // Managed by new sidebar structure
  },
  {
    name: 'Notificaciones',
    nameKey: 'navigation.notifications',
    path: ':companyId/notificaciones',
    layout: '/empresa',
    component: (
      <Suspense fallback={<div />}>
        <NotificationsPage />
      </Suspense>
    ),
    showInNavbar: false,
    showInSidebar: false,
  },
  {
    name: 'Documentos',
    nameKey: 'navigation.documents',
    path: ':companyId/documentos',
    layout: '/empresa',
    component: (
      <Suspense fallback={<div />}>
        <DocumentsPage />
      </Suspense>
    ),
    showInNavbar: false,
    showInSidebar: false,
  },
  // --- Company-scoped service pages ---
  {
    name: 'Cuenta Bancaria',
    nameKey: 'navigation.bankAccount',
    path: ':companyId/banking',
    layout: '/empresa',
    component: (
      <Suspense fallback={<div />}>
        <BankAccountPage />
      </Suspense>
    ),
    showInNavbar: false,
    showInSidebar: false,
  },
  {
    name: 'Cuenta Bancaria',
    nameKey: 'navigation.bankAccount',
    path: 'banking',
    layout: '/empresa',
    component: (
      <Suspense fallback={<div />}>
        <BankAccountPage />
      </Suspense>
    ),
    showInNavbar: false,
    showInSidebar: false,
  },
  {
    name: 'Impuestos',
    nameKey: 'navigation.taxes',
    path: ':companyId/impuestos',
    layout: '/empresa',
    component: <TaxesRedirect />,
    showInNavbar: false,
    showInSidebar: false,
  },
  {
    name: 'Editar Miembros',
    nameKey: null,
    path: ':companyId/servicios/miembros',
    layout: '/empresa',
    component: (
      <Suspense fallback={<div />}>
        <MemberChangesPage />
      </Suspense>
    ),
    showInNavbar: false,
    showInSidebar: false,
  },
  {
    name: 'Impuestos',
    nameKey: 'navigation.taxes',
    path: 'impuestos',
    layout: '/empresa',
    component: <TaxesRedirect />,
    showInNavbar: false,
    showInSidebar: false,
  },
  // --- Perks page ---
  {
    name: 'Perks',
    nameKey: 'navigation.perks',
    path: ':companyId/perks',
    layout: '/empresa',
    component: (
      <Suspense fallback={<div />}>
        <PerksPage />
      </Suspense>
    ),
    showInNavbar: false,
    showInSidebar: false,
  },
  // --- Account pages ---
  {
    name: 'Ayuda', // Fallback
    nameKey: 'help',
    path: 'soporte',
    layout: '/empresa',
    icon: <MdSupport className="text-inherit h-5 w-5" />,
    component: (
      <Suspense fallback={<div />}>
        <HelpCenter />
      </Suspense>
    ),
    showInNavbar: false,
    showInSidebar: false, // Managed by new sidebar structure
  },
  {
    name: 'Configuración', // Fallback
    nameKey: 'settings',
    path: 'cuenta',
    layout: '/empresa',
    icon: <MdSettings className="text-inherit h-5 w-5" />,
    component: (
      <Suspense fallback={<div />}>
        <AccountSettings />
      </Suspense>
    ),
    showInNavbar: false,
    showInSidebar: false, // Managed by new sidebar structure
  },
];

export default routes;
